import { useId, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { sportLabel } from "../../lib/labels";
import { PRECIO_TEXTO, PRIMER_TORNEO_TEXTO } from "./content";
import { CtaBand } from "./HomePage";
import { usePageMeta } from "./hooks";
import { TournamentCards } from "./TournamentCards";
import {
  DIRECTORY_SPORTS,
  QUERY_PARAM,
  SPORT_PARAM,
  filterDirectory,
  useTournamentDirectory,
} from "./useTournamentDirectory";
import "./TournamentsPage.css";

/** /torneos: directorio de torneos con inscripciones abiertas. El texto y el
 * deporte viven en la URL (?q=&deporte=) para que el buscador del inicio y
 * los enlaces compartidos lleguen ya filtrados. */
export function TournamentsPage() {
  usePageMeta("tournaments");
  const id = useId();
  const { tournaments, status } = useTournamentDirectory();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get(QUERY_PARAM) ?? "";
  const sport = searchParams.get(SPORT_PARAM) ?? "";

  const sports = useMemo(() => {
    const keys = new Set(DIRECTORY_SPORTS);
    tournaments.forEach((tournament) => keys.add(tournament.sport || "tenis"));
    if (sport) keys.add(sport);
    return Array.from(keys);
  }, [sport, tournaments]);

  const filtered = useMemo(() => filterDirectory(tournaments, query, sport), [query, sport, tournaments]);
  const hasFilters = Boolean(query.trim() || sport);

  function updateParam(name: string, value: string) {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) next.set(name, value);
        else next.delete(name);
        return next;
      },
      { replace: true },
    );
  }

  function clearFilters() {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(QUERY_PARAM);
        next.delete(SPORT_PARAM);
        return next;
      },
      { replace: true },
    );
  }

  const countText =
    status !== "ready"
      ? ""
      : filtered.length === 1
        ? "1 torneo con inscripciones abiertas"
        : `${filtered.length} torneos con inscripciones abiertas`;

  return (
    <main className="t123-tournaments">
      <section className="t123-subhero">
        <div className="t123-shell">
          <span className="t123-kicker">Torneos públicos</span>
          <h1>Torneos con inscripciones abiertas</h1>
          <p>Elige tu torneo, revisa sus categorías e inscríbete desde el celular.</p>
        </div>
      </section>

      <section className="t123-directory" aria-labelledby={`${id}-results`}>
        <div className="t123-shell">
          <form className="t123-directory__filters" role="search" onSubmit={(event) => event.preventDefault()}>
            <label>
              <span>Buscar</span>
              <input
                type="search"
                value={query}
                onChange={(event) => updateParam(QUERY_PARAM, event.target.value)}
                placeholder="Torneo, sede u organizador"
                autoComplete="off"
                enterKeyHint="search"
                maxLength={120}
              />
            </label>
            <label>
              <span>Deporte</span>
              <select className="t123-select" value={sport} onChange={(event) => updateParam(SPORT_PARAM, event.target.value)}>
                <option value="">Todos</option>
                {sports.map((key) => (
                  <option key={key} value={key}>
                    {sportLabel(key)}
                  </option>
                ))}
              </select>
            </label>
          </form>

          <div className="t123-tournaments__bar">
            <h2 className="t123-tournaments__count" id={`${id}-results`} aria-live="polite">
              {countText || "Torneos"}
            </h2>
            {hasFilters ? (
              <button type="button" className="t123-tournaments__clear" onClick={clearFilters}>
                Quitar filtros
              </button>
            ) : null}
          </div>

          <TournamentCards
            tournaments={filtered}
            status={status}
            emptyText={
              tournaments.length === 0
                ? "Pronto verás aquí los torneos con inscripciones abiertas."
                : "No encontramos torneos con esos filtros."
            }
          />
        </div>
      </section>

      <CtaBand
        title="¿Organizas torneos?"
        copy={`Publica el tuyo en Torneos123. ${PRIMER_TORNEO_TEXTO}; después, ${PRECIO_TEXTO}.`}
        primary={{ to: "/organizador#demo", label: "Pedir demo" }}
        secondary={{ to: "/organizador", label: "Conoce cómo funciona" }}
      />
    </main>
  );
}
